import React from "react";
import { Link } from "react-router-dom";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import CONSTANT from "./../../constant";
import Loader from "./../../component/common/Loader";
import AparEmployeeHeader from "./../../component/common/AparEmployeeHeader";
import { getUserSummaryList } from "./../../actions/UserAction";
import { saveEmployeeAuthority, getEmployeeAuthorityDetail, getSelectedOrActiveCycle } from "../../actions/AppraisalCycleAction";
import { getSelectedCycleId, formatDateDDMMYY } from "../../utils";

class ReportingAuthority extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      employeeCode: props.match.params.code,
      showLoader: false,
      employeeList: [],
      isViewMode: false,
      activeCycleStartDate: "",
      activeCycleEndDate: "",

      // ── Primary / Original Assignment ──
      reportingManager:   { name: "reportingManager",   value: [], error: "", isRequired: true },
      reviewingManager:   { name: "reviewingManager",   value: [], error: "", isRequired: true },
      acceptingAuthority: { name: "acceptingAuthority", value: [], error: "", isRequired: true },
      originalStartDate:  { value: "", error: "" },
      originalEndDate:    { value: "", error: "" },

      // ── Transfer Records ──
      transferList: [],
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    document.title = "Reporting & Reviewing Authority - " + CONSTANT.organisation.name;
    this.loadData();
  }

  loadData = () => {
    this.setState({ showLoader: true });
    Promise.all([getUserSummaryList(), getSelectedOrActiveCycle()]).then(
      ([userRes, cycleRes]: any) => {
        const employeeList = userRes.result || [];
        const activeCycleStartDate = cycleRes?.result?.start_date ? cycleRes.result.start_date.split("T")[0] : "";
        const activeCycleEndDate   = cycleRes?.result?.end_date   ? cycleRes.result.end_date.split("T")[0]   : "";
        this.setState(
          { employeeList, activeCycleStartDate, activeCycleEndDate, showLoader: false },
          () => { this.fetchAuthorityData(); }
        );
      }
    );
  };

  fetchAuthorityData = () => {
    const { employeeCode, employeeList, activeCycleStartDate, activeCycleEndDate } = this.state;

    getEmployeeAuthorityDetail({ employeeCode }).then((res: any) => {
      const data = res?.result;
      if (!data || !data.reportingManager) {
        if (activeCycleStartDate) {
          this.setState({
            originalStartDate: { value: activeCycleStartDate, error: "" },
            originalEndDate:   { value: activeCycleEndDate,   error: "" },
          });
        }
        return;
      }

      this.setState({
        reportingManager:   { ...this.state.reportingManager,   value: employeeList.filter((e: any) => String(e.code) === String(data.reportingManager)) },
        reviewingManager:   { ...this.state.reviewingManager,   value: employeeList.filter((e: any) => String(e.code) === String(data.reviewingManager)) },
        acceptingAuthority: { ...this.state.acceptingAuthority, value: employeeList.filter((e: any) => String(e.code) === String(data.acceptingAuthority)) },
        originalStartDate:  { value: data.startDate || activeCycleStartDate || "", error: "" },
        originalEndDate:    { value: data.endDate   || activeCycleEndDate   || "", error: "" },
        transferList: (data.transferList || []).map((t: any, index: number) => ({
          id: Date.now() + index,
          startDate:          t.startDate || "",
          endDate:            t.endDate   || (index === (data.transferList.length - 1) ? activeCycleEndDate : ""),
          reportingManager:   employeeList.filter((e: any) => String(e.code) === String(t.reportingManager)),
          reviewingManager:   employeeList.filter((e: any) => String(e.code) === String(t.reviewingManager)),
          acceptingAuthority: employeeList.filter((e: any) => String(e.code) === String(t.acceptingAuthority)),
          isPrefetched: true,
          isLocked:     t.isLocked ?? false,
          errors: {},
        })),
        isViewMode: data.isLocked ?? false,
      });
    });
  };

  // ── helpers for transfer list ──
  addTransfer = () => {
    const newEntry = {
      id: Date.now(),
      startDate:          "",
      endDate:            "",
      reportingManager:   [],
      reviewingManager:   [],
      acceptingAuthority: [],
      isPrefetched: false,
      errors: {},
    };
    this.setState((prev: any) => ({
      transferList: [...prev.transferList, newEntry],
    }));
  };

  removeTransfer = (id: number) => {
    this.setState((prev: any) => ({
      transferList: prev.transferList.filter((t: any) => t.id !== id),
    }));
  };

  onTransferFieldChange = (id: number, field: string, value: any) => {
    this.setState((prev: any) => ({
      transferList: prev.transferList.map((t: any) =>
        t.id === id
          ? { ...t, [field]: value, errors: { ...t.errors, [field]: "" } }
          : t
      ),
    }));
  };

  onTypeaheadChange = (fieldName: string, selected: any[]) => {
    this.setState((prev: any) => ({
      [fieldName]: { ...prev[fieldName], value: selected, error: "" },
    }));
  };

  onOriginalDateChange = (field: "originalStartDate" | "originalEndDate", value: string) => {
    this.setState({ [field]: { value, error: "" } });
  };

  // ── render ──
  render() {
    const {
      employeeCode, employeeList, showLoader,
      reportingManager, reviewingManager, acceptingAuthority,
      originalStartDate, originalEndDate,
      transferList, isViewMode,
    } = this.state;

    const employee = employeeList.find((e: any) => String(e.code) === String(employeeCode));

    const prefetchedTransfers = transferList.filter((t: any) => t.isPrefetched);
    const lastPrefetchedId    = prefetchedTransfers.length > 0
      ? prefetchedTransfers[prefetchedTransfers.length - 1].id
      : null;

    return (
      <div className="container-fluid mb-3">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">

                {/* ── Header ── */}
                <div className="row mb-3">
                  <div className="col-md-8">
                    <h1 className="page-title">Reporting &amp; Reviewing Authority</h1>
                  </div>
                  <div className="col-md-4 text-right">
                    <Link
                      to={CONSTANT.component.manageEmployee.url}
                      className="btn btn-sm cancel-button"
                    >
                      <i className="fa fa-arrow-left mr-2" />
                      Back to List
                    </Link>
                  </div>
                </div>

                <AparEmployeeHeader
                  employeeName={employee?.name}
                  employeeNumber={employee?.employeeNumber}
                />

                <form onSubmit={this.onSubmit}>

                  {isViewMode && (
                    <div className="alert alert-warning py-2 mb-3">
                      <i className="fa fa-lock mr-2" />
                      Authority is locked — KPI approval is in progress or self-appraisal has started.
                    </div>
                  )}

                  {/* ── Section 1: Original Authorities ── */}
                  <div className="row mb-2">
                    <div className="col-md-12">
                      <h5 className="text-muted">
                        <i className="fa fa-users mr-2" />
                        Original Authorities
                        <small className="ml-2 text-muted font-weight-normal">
                          (valid from start of appraisal year)
                        </small>
                      </h5>
                      <hr />
                    </div>
                  </div>

                  {/* Original Start & End Date */}
                  <div className="row">
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>Start Date <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className={originalStartDate.error ? "form-control is-invalid" : "form-control"}
                          value={originalStartDate.value}
                          disabled={isViewMode}
                          onChange={(e) => this.onOriginalDateChange("originalStartDate", e.target.value)}
                        />
                        {originalStartDate.error && (
                          <div className="invalid-feedback">{originalStartDate.error}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>End Date</label>
                        <input
                          type="date"
                          className={originalEndDate.error ? "form-control is-invalid" : "form-control"}
                          value={originalEndDate.value}
                          disabled={isViewMode && prefetchedTransfers.length > 0}
                          min={originalStartDate.value || undefined}
                          onChange={(e) => this.onOriginalDateChange("originalEndDate", e.target.value)}
                        />
                        {originalEndDate.error && (
                          <div className="invalid-feedback">{originalEndDate.error}</div>
                        )}
                        <small className="text-muted">Optional — leave blank if ongoing</small>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Reporting Manager <span className="text-danger">*</span></label>
                        <Typeahead
                          id="reporting-manager"
                          labelKey={(o: any) => `${o.name} (${o.employeeNumber})`}
                          options={employeeList}
                          selected={reportingManager.value}
                          onChange={(s: any) => this.onTypeaheadChange("reportingManager", s)}
                          placeholder="Search and select"
                          isInvalid={!!reportingManager.error}
                          disabled={isViewMode}
                          flip
                        />
                        {reportingManager.error && (
                          <div className="text-danger small mt-1">{reportingManager.error}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Reviewing Manager <span className="text-danger">*</span></label>
                        <Typeahead
                          id="reviewing-manager"
                          labelKey={(o: any) => `${o.name} (${o.employeeNumber})`}
                          options={employeeList}
                          selected={reviewingManager.value}
                          onChange={(s: any) => this.onTypeaheadChange("reviewingManager", s)}
                          placeholder="Search and select"
                          isInvalid={!!reviewingManager.error}
                          disabled={isViewMode}
                          flip
                        />
                        {reviewingManager.error && (
                          <div className="text-danger small mt-1">{reviewingManager.error}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Accepting Authority <span className="text-danger">*</span></label>
                        <Typeahead
                          id="accepting-authority"
                          labelKey={(o: any) => `${o.name} (${o.employeeNumber})`}
                          options={employeeList}
                          selected={acceptingAuthority.value}
                          onChange={(s: any) => this.onTypeaheadChange("acceptingAuthority", s)}
                          placeholder="Search and select"
                          isInvalid={!!acceptingAuthority.error}
                          disabled={isViewMode}
                          flip
                        />
                        {acceptingAuthority.error && (
                          <div className="text-danger small mt-1">{acceptingAuthority.error}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Section 2: Period Records ── */}
                  <div className="row mb-2 mt-4">
                    <div className="col-md-6">
                      <h5 className="text-muted">
                        <i className="fa fa-exchange mr-2" />
                        Period Records
                        {transferList.length > 0 && (
                          <span className="badge badge-info ml-2">
                            {transferList.length} Period{transferList.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </h5>
                    </div>
                    <div className="col-md-6 text-right">
                      <button
                        type="button"
                        className="btn btn-sm submit-button"
                        onClick={this.addTransfer}
                      >
                        <i className="fa fa-plus-circle mr-2" />
                        Add Period
                      </button>
                    </div>
                  </div>
                  <hr />

                  {/* No periods yet */}
                  {transferList.length === 0 && (
                    <div className="row">
                      <div className="col-md-12">
                        <div className="alert alert-success">
                          <i className="fa fa-check-circle mr-2" />
                          No periods recorded — original authorities above apply for the entire appraisal year.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Period cards */}
                  {transferList.map((transfer: any, index: number) => (
                    <div key={transfer.id} className="card mb-3 border">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <strong>
                          <i className="fa fa-exchange mr-2 text-warning" />
                          Period #{index + 1}
                          {transfer.startDate && (
                            <span className="ml-2 text-muted font-weight-normal">
                              — {formatDateDDMMYY(transfer.startDate)}{transfer.endDate ? ` to ${formatDateDDMMYY(transfer.endDate)}` : " onwards"}
                            </span>
                          )}
                        </strong>
                        {!transfer.isPrefetched && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => this.removeTransfer(transfer.id)}
                          >
                            <i className="fa fa-trash mr-1" /> Remove
                          </button>
                        )}
                      </div>

                      <div className="card-body">
                        {/* Start Date & End Date */}
                        <div className="row">
                          <div className="col-md-3">
                            <div className="form-group">
                              <label>
                                Start Date <span className="text-danger">*</span>
                              </label>
                              <input
                                type="date"
                                className={
                                  transfer.errors?.startDate
                                    ? "form-control is-invalid"
                                    : "form-control"
                                }
                                value={transfer.startDate}
                                disabled={transfer.isLocked}
                                onChange={(e) =>
                                  this.onTransferFieldChange(transfer.id, "startDate", e.target.value)
                                }
                              />
                              {transfer.errors?.startDate && (
                                <div className="invalid-feedback">{transfer.errors.startDate}</div>
                              )}
                              <small className="text-muted">New authorities apply from this date</small>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-group">
                              <label>End Date</label>
                              <input
                                type="date"
                                className={
                                  transfer.errors?.endDate
                                    ? "form-control is-invalid"
                                    : "form-control"
                                }
                                value={transfer.endDate}
                                disabled={transfer.isLocked}
                                min={transfer.startDate || undefined}
                                onChange={(e) =>
                                  this.onTransferFieldChange(transfer.id, "endDate", e.target.value)
                                }
                              />
                              {transfer.errors?.endDate && (
                                <div className="invalid-feedback">{transfer.errors.endDate}</div>
                              )}
                              <small className="text-muted">Optional — leave blank if ongoing</small>
                            </div>
                          </div>
                        </div>

                        {/* New authorities */}
                        <div className="row">
                          <div className="col-md-4">
                            <div className="form-group">
                              <label>
                                New Reporting Manager <span className="text-danger">*</span>
                              </label>
                              <Typeahead
                                id={`new-rm-${transfer.id}`}
                                labelKey={(o: any) => `${o.name} (${o.employeeNumber})`}
                                options={employeeList}
                                selected={transfer.reportingManager}
                                onChange={(s: any) =>
                                  this.onTransferFieldChange(transfer.id, "reportingManager", s)
                                }
                                placeholder="Search and select"
                                isInvalid={!!transfer.errors?.reportingManager}
                                disabled={transfer.isLocked}
                                flip
                              />
                              {transfer.errors?.reportingManager && (
                                <div className="text-danger small mt-1">
                                  {transfer.errors.reportingManager}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="form-group">
                              <label>
                                New Reviewing Manager <span className="text-danger">*</span>
                              </label>
                              <Typeahead
                                id={`new-rvm-${transfer.id}`}
                                labelKey={(o: any) => `${o.name} (${o.employeeNumber})`}
                                options={employeeList}
                                selected={transfer.reviewingManager}
                                onChange={(s: any) =>
                                  this.onTransferFieldChange(transfer.id, "reviewingManager", s)
                                }
                                placeholder="Search and select"
                                isInvalid={!!transfer.errors?.reviewingManager}
                                disabled={transfer.isLocked}
                                flip
                              />
                              {transfer.errors?.reviewingManager && (
                                <div className="text-danger small mt-1">
                                  {transfer.errors.reviewingManager}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="form-group">
                              <label>
                                New Accepting Authority <span className="text-danger">*</span>
                              </label>
                              <Typeahead
                                id={`new-aa-${transfer.id}`}
                                labelKey={(o: any) => `${o.name} (${o.employeeNumber})`}
                                options={employeeList}
                                selected={transfer.acceptingAuthority}
                                onChange={(s: any) =>
                                  this.onTransferFieldChange(transfer.id, "acceptingAuthority", s)
                                }
                                placeholder="Search and select"
                                isInvalid={!!transfer.errors?.acceptingAuthority}
                                disabled={transfer.isLocked}
                                flip
                              />
                              {transfer.errors?.acceptingAuthority && (
                                <div className="text-danger small mt-1">
                                  {transfer.errors.acceptingAuthority}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* ── Action Buttons ── */}
                  <div className="row mt-4">
                    <div className="col-md-3">
                      <Link
                        to={CONSTANT.component.manageEmployee.url}
                        className="btn btn-block cancel-button btn-sm"
                      >
                        Cancel
                      </Link>
                    </div>
                    <div className="col-md-6" />
                    <div className="col-md-3">
                      <button type="submit" className="btn btn-block submit-button btn-sm">
                        Save Details
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {showLoader && <Loader />}
      </div>
    );
  }

  validateForm = () => {
    const { reportingManager, reviewingManager, acceptingAuthority, originalStartDate, originalEndDate, transferList } = this.state;
    let isValid = true;

    // Validate original section
    if (!reportingManager.value.length) {
      this.setState((p: any) => ({ reportingManager: { ...p.reportingManager, error: "Reporting manager is required" } }));
      isValid = false;
    }
    if (!reviewingManager.value.length) {
      this.setState((p: any) => ({ reviewingManager: { ...p.reviewingManager, error: "Reviewing manager is required" } }));
      isValid = false;
    }
    if (!acceptingAuthority.value.length) {
      this.setState((p: any) => ({ acceptingAuthority: { ...p.acceptingAuthority, error: "Accepting authority is required" } }));
      isValid = false;
    }
    if (!originalStartDate.value) {
      this.setState({ originalStartDate: { ...originalStartDate, error: "Start date is required" } });
      isValid = false;
    }
    if (originalEndDate.value && originalStartDate.value && originalEndDate.value <= originalStartDate.value) {
      this.setState({ originalEndDate: { ...originalEndDate, error: "End date must be after start date" } });
      isValid = false;
    }

    // If new transfers exist, last period must have an end date
    const prefetchedTransfers = transferList.filter((t: any) => t.isPrefetched);
    const hasNewTransfers     = transferList.some((t: any) => !t.isPrefetched);
    if (hasNewTransfers) {
      if (prefetchedTransfers.length === 0 && !originalEndDate.value) {
        this.setState({ originalEndDate: { ...originalEndDate, error: "End date is required before adding a new period" } });
        isValid = false;
      } else if (prefetchedTransfers.length > 0) {
        const last = prefetchedTransfers[prefetchedTransfers.length - 1];
        if (!last.endDate) {
          this.setState((prev: any) => ({
            transferList: prev.transferList.map((t: any) =>
              t.id === last.id
                ? { ...t, errors: { ...t.errors, endDate: "End date is required before adding a new period" } }
                : t
            ),
          }));
          isValid = false;
        }
      }
    }

    // Build ordered list of all periods for sequential date validation
    // Each entry: { endDate } — the end date of that period in order
    const newTransfers = transferList
      .filter((t: any) => !t.isPrefetched)
      .sort((a: any, b: any) => (a.startDate > b.startDate ? 1 : -1));

    // previousEndDate per new transfer: original → prefetched transfers → new transfers in order
    const getPreviousEndDate = (index: number): string => {
      if (index === 0) {
        // previous of first new transfer = last prefetched's endDate, or originalEndDate
        return prefetchedTransfers.length > 0
          ? prefetchedTransfers[prefetchedTransfers.length - 1].endDate
          : originalEndDate.value;
      }
      return newTransfers[index - 1].endDate || "";
    };

    // Validate transfer records
    const updatedTransferList = transferList.map((t: any) => {
      const errors: any = {};
      if (!t.isPrefetched) {
        const idx         = newTransfers.findIndex((n: any) => n.id === t.id);
        const prevEndDate = getPreviousEndDate(idx);

        if (!t.startDate) {
          errors.startDate = "Start date is required";
        } else if (prevEndDate && t.startDate <= prevEndDate) {
          errors.startDate = `Start date must be after previous period's end date (${prevEndDate})`;
        }

        if (t.endDate && t.startDate && t.endDate <= t.startDate)
          errors.endDate = "End date must be after start date";

        if (!t.reportingManager.length)   errors.reportingManager   = "New reporting manager is required";
        if (!t.reviewingManager.length)   errors.reviewingManager   = "New reviewing manager is required";
        if (!t.acceptingAuthority.length) errors.acceptingAuthority = "New accepting authority is required";
        if (Object.keys(errors).length) isValid = false;
      }
      return { ...t, errors };
    });

    // Check for duplicate start dates across new transfers
    const startDates = transferList
      .filter((t: any) => !t.isPrefetched)
      .map((t: any) => t.startDate)
      .filter(Boolean);
    if (startDates.length !== new Set(startDates).size) {
      alert("Two periods cannot have the same start date. Please check period dates.");
      isValid = false;
    }

    this.setState({ transferList: updatedTransferList });
    return isValid;
  };

  onSubmit = (e: any) => {
    e.preventDefault();
    if (!this.validateForm()) return;

    // Authority mapping is a setup screen with no opened record, so the cycle
    // comes from the cycle selected in the topbar. Require it explicitly so the
    // mapping can never silently land in the backend's fallback cycle.
    const selectedCycleId = getSelectedCycleId();
    if (!selectedCycleId) {
      alert("Please select an appraisal cycle in the top bar before saving.");
      return;
    }

    this.setState({ showLoader: true });
    const st = this.state;

    const model = {
      appraisalCycleId: selectedCycleId,
      employeeCode: st.employeeCode,
      reportingManager:   st.reportingManager.value[0]?.code,
      reviewingManager:   st.reviewingManager.value[0]?.code,
      acceptingAuthority: st.acceptingAuthority.value[0]?.code,
      startDate:          st.originalStartDate.value,
      endDate:            st.originalEndDate.value || null,
      transferList: st.transferList
        .sort((a: any, b: any) => (a.startDate > b.startDate ? 1 : -1))
        .map((t: any) => ({
          startDate:          t.startDate,
          endDate:            t.endDate || null,
          reportingManager:   t.reportingManager[0]?.code,
          reviewingManager:   t.reviewingManager[0]?.code,
          acceptingAuthority: t.acceptingAuthority[0]?.code,
        })),
    };

    saveEmployeeAuthority(model)
      .then((res: any) => {
        this.setState({ showLoader: false });
        if (res.status === CONSTANT.static.apiStatus.success) {
          alert("Reporting authority details saved successfully!");
          this.props.history.push(CONSTANT.component.manageEmployee.url);
        } else {
          alert(res.error || "Something went wrong");
        }
      })
      .catch((err: any) => {
        this.setState({ showLoader: false });
        alert("Server error occurred");
        console.error(err);
      });

    console.log("Authority Model:", model);
  };
}

export default ReportingAuthority;